import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { ArrowLeft, ArrowRight, Camera01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { QEPhoto } from "./types"
import type { QuickEstimateActions } from "./use-quick-estimate-state"
import type { UseQECapture } from "./use-qe-capture"
import {
  PANELS_BY_ID,
  SEVERITY_LABEL,
  DAMAGE_TYPE_LABEL,
  type DamageTag,
} from "./damage-tag/panels"

// Guided per-panel capture (quick-estimate). Walks the advisor through ONLY
// the panels they tagged on the 3D car — one panel at a time — so every photo
// arrives pre-attributed to a panel + side. Reuses the shared capture pipeline
// (preflight, in-page camera, AI quality eval) via `capture`; we just set the
// capture context so each shot inherits this panel's declared side + id.

interface StepGuidedPhotosProps {
  damageTags: DamageTag[]
  photos: QEPhoto[]
  actions: QuickEstimateActions
  capture: UseQECapture
  onBack: () => void
  onContinue: () => void
}

export function StepGuidedPhotos({
  damageTags,
  photos,
  actions,
  capture,
  onBack,
  onContinue,
}: StepGuidedPhotosProps) {
  const [index, setIndex] = useState(0)
  const panel = damageTags[index]

  // Defensive: the Damage step requires ≥1 tag to advance, so this
  // shouldn't render — but never crash if it does.
  if (!panel) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-[14px] text-zinc-500">
          Tag a damaged panel first, then we’ll guide your photos.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-10 items-center gap-1.5 text-[13px] font-semibold text-emerald-700"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to tagging
        </button>
      </div>
    )
  }

  const photosFor = (panelId: string) =>
    photos.filter((p) => p.taggedPanelId === panelId)
  const panelPhotos = photosFor(panel.panelId)
  const coveredCount = damageTags.filter(
    (t) => photosFor(t.panelId).length > 0,
  ).length
  const isLast = index === damageTags.length - 1

  const take = () => {
    capture.setCaptureContext({ side: panel.side, taggedPanelId: panel.panelId })
    capture.triggerCapture()
  }
  const removePhoto = (id: string) => {
    actions.removePhoto(id)
    capture.forgetFile(id)
  }
  const goNext = () => {
    capture.setCaptureContext(null)
    if (isLast) onContinue()
    else setIndex((i) => i + 1)
  }
  const goBack = () => {
    capture.setCaptureContext(null)
    if (index === 0) onBack()
    else setIndex((i) => i - 1)
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          Step 3 of 4 · Photos
        </p>
        <h2 className="text-2xl font-bold leading-tight tracking-tight text-zinc-900 md:text-[28px]">
          Photograph the {panel.label.toLowerCase()}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
          Get a clear shot of the damage — add another angle if it helps. We’ll
          send these tagged so the estimate knows exactly what it’s seeing.
        </p>
      </header>

      {/* Panel progress — one segment per tagged panel. */}
      <div className="flex items-center gap-1.5" aria-hidden>
        {damageTags.map((t, i) => {
          const done = photosFor(t.panelId).length > 0
          const current = i === index
          return (
            <div
              key={t.panelId}
              className={cx(
                "h-1.5 flex-1 rounded-full transition-colors duration-300",
                done
                  ? "bg-emerald-500"
                  : current
                    ? "bg-emerald-300"
                    : "bg-zinc-200",
              )}
            />
          )
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={panel.panelId}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {/* Context chip — what the advisor declared on the 3D car. */}
          <div className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1.5 text-[12px] font-semibold text-zinc-600">
            {PANELS_BY_ID[panel.panelId]?.label ?? panel.label}
            <span className="font-normal text-zinc-400">
              · {SEVERITY_LABEL[panel.severity]} {DAMAGE_TYPE_LABEL[panel.damageType]}
            </span>
          </div>

          {/* Captured shots for this panel + any in-flight tiles. */}
          {(panelPhotos.length > 0 || capture.inflight.length > 0) && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {panelPhotos.map((p) => (
                <PhotoThumb key={p.id} photo={p} onRemove={() => removePhoto(p.id)} />
              ))}
              {capture.inflight.map((t) => (
                <div
                  key={t.id}
                  className="relative aspect-square overflow-hidden rounded-xl border border-zinc-200"
                >
                  <img src={t.previewUrl} alt="" className="h-full w-full object-cover opacity-60" />
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="size-5 animate-spin rounded-full border-2 border-white/60 border-t-white" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Capture button */}
          <button
            type="button"
            onClick={take}
            className="flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-zinc-900 text-white shadow-xl shadow-zinc-900/20 transition-all duration-150 hover:bg-black hover:shadow-zinc-900/30"
          >
            <Camera01 className="size-6" aria-hidden />
            <span className="text-[17px] font-semibold">
              {panelPhotos.length === 0 ? "Take photo" : "Add another angle"}
            </span>
          </button>
        </motion.div>
      </AnimatePresence>

      {/* Advance / skip */}
      <div className="space-y-2 pt-1">
        {panelPhotos.length > 0 ? (
          <button
            type="button"
            onClick={goNext}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 text-[15px] font-semibold text-white shadow-lg shadow-emerald-600/20 transition-all duration-150 hover:bg-emerald-700"
          >
            {isLast ? "Done — review" : "Next panel"}
            <ArrowRight className="size-4" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={goNext}
            className="mx-auto block text-[12.5px] font-medium text-zinc-400 transition-colors hover:text-zinc-600"
          >
            {isLast ? "Skip — review without a photo" : "Skip this panel"}
          </button>
        )}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex h-9 items-center gap-1.5 text-[12.5px] font-medium text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            {index === 0 ? "Back to tagging" : "Previous panel"}
          </button>
          <span className="text-[11.5px] font-medium text-zinc-400">
            {coveredCount} of {damageTags.length} photographed
          </span>
        </div>
      </div>
    </div>
  )
}

function PhotoThumb({
  photo,
  onRemove,
}: {
  photo: QEPhoto
  onRemove: () => void
}) {
  const good = photo.verdict === "good" || (photo.qualified && photo.uploadedUrl.length > 0)
  const failed = photo.verdict === "eval-failed"
  return (
    <div className="group relative aspect-square overflow-hidden rounded-xl border border-zinc-200">
      <img src={photo.previewUrl} alt="" className="h-full w-full object-cover" />
      <span
        className={cx(
          "absolute bottom-1 left-1 size-2.5 rounded-full ring-2 ring-white",
          failed ? "bg-zinc-400" : good ? "bg-emerald-500" : "bg-amber-500",
        )}
        aria-hidden
      />
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-zinc-900/70 text-[11px] font-bold text-white opacity-0 transition-opacity group-hover:opacity-100"
        aria-label="Remove photo"
      >
        ×
      </button>
    </div>
  )
}
